import { useDispatch, useSelector } from 'react-redux';

// Design System
import { destroyForm } from 'jl-design-system/redux/actions/form/formActions';

// Types
import { AppDispatch } from 'types/RootState.types';
import { AddNewCardProps } from './AddNewCard.types';

// Config
import addNewCardState from './AddNewCard.state';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { setSelectedPaymentType } from '@redux/actions/payment/paymentActions';
import { SAVED_CARD_DETAILS_FORM_ID } from '@utils/form/configs/savedCardDetails';
import { scrollToElement } from '@utils/index';

// Components
import CardDetails from '@components/add-payment-details/card-details';
import { BillingDetails } from '@components/add-payment-details/card-details/billing-details';
import PaymentOption from '@components/add-payment-details/payment-option';
import Container from '@components/container';
import OrderTotals from '@components/order-totals';
import PaymentButton from '@components/payment-button';
import StripePaymentProvider from '@components/payments/stripe/stripe-payment-provider';
import ThreeDSecureWarning from '@components/three-d-secure-warning';

// Styles
import styles from './add-new-card.scss';

const AddNewCard = ({
  desktopResolution,
  disabled = false,
  selectedPaymentType,
  savedPaymentCards = [],
  resetAfterError,
  showOrderTotals,
}: AddNewCardProps) => {
  const dispatch: AppDispatch = useDispatch();
  const { stripeCheckoutFlowEnabled } = useSelector(addNewCardState);

  const togglePaymentOption = (paymentType = '') => {
    const clickedPaymentOptionIsAlreadySelected = selectedPaymentType === paymentType;

    if (!clickedPaymentOptionIsAlreadySelected) {
      // Ensure saved card details forms are destroyed before selecting a new payment type
      dispatch(destroyForm(SAVED_CARD_DETAILS_FORM_ID));
      setTimeout(() => {
        dispatch(setSelectedPaymentType({ paymentType, savedPaymentCards }));

        scrollToElement({
          id: paymentType,
        });
      }, 0);
    }
  };

  return (
    <Container marginTop="1">
      <PaymentOption
        ariaLabel="Accepted cards: John Lewis Partnership Card, VISA, Mastercard and AMEX"
        data-testid={`payment-option-${paymentTypeConstants.CREDIT_CARD}`}
        desktopResolution={desktopResolution}
        disabled={disabled}
        icons={[styles.cardIcon]}
        id={paymentTypeConstants.CREDIT_CARD}
        label="Credit / Debit card"
        name="paymentOption"
        onClick={togglePaymentOption}
        selected={selectedPaymentType === paymentTypeConstants.CREDIT_CARD}
      >
        <div className={styles.newCardButtonContainer}>
          {stripeCheckoutFlowEnabled ? (
            <>
              <Container marginBottom="1" paddingTop="2" paddingX="2">
                <ThreeDSecureWarning />
              </Container>
              <StripePaymentProvider
                afterFormContent={
                  <>
                    <Container borderBottom="black" marginBottom="2" padding="2">
                      <BillingDetails disabled={disabled} />
                    </Container>
                    <Container marginBottom="2" paddingX="2">
                      {showOrderTotals && <OrderTotals containerWide />}
                    </Container>
                  </>
                }
                classNames={{
                  afterFormContent: styles.afterFormContent,
                  container: styles.container,
                  form: styles.form,
                  paymentButton: `${styles.paymentButton} mb-2 px-2`,
                  paymentElement: `${styles.paymentElement} p-2`,
                }}
                id="addNewCard"
              />
            </>
          ) : (
            <>
              <div className={styles.newCardDetails}>
                <CardDetails disabled={disabled} isNewCard resetAfterError={resetAfterError} />
              </div>
              <div className={styles.paymentButtonContainer}>
                {showOrderTotals && <OrderTotals containerWide />}
                <PaymentButton disabled={disabled} />
              </div>
            </>
          )}
        </div>
      </PaymentOption>
    </Container>
  );
};

export default AddNewCard;
